export type Coordinate = { lat: number; lng: number };
export type VentKind = 'soffit' | 'edge' | 'ridge' | 'box' | 'turbine' | 'gable' | 'powered' | 'other';
export type VentUnit = 'each' | 'lf' | 'sqft';
export type Stage = 'existing' | 'proposed';
export interface Vent {
  id: string; name: string; kind: VentKind; role: 'intake' | 'exhaust'; unit: VentUnit;
  quantity: string; nfa_per_unit: string | null; cfm_per_unit: string | null;
  makeup_nfa_per_unit: string | null; available_quantity: string | null;
  location: string; rating_source: string; blocked: boolean;
  polygon: Coordinate[]; mapped_area_sqft: string | null;
}
export interface AtticSpace {
  id: string; name: string; area_sqft: string; ratio: 150 | 300; exhaust_percent: number;
  reduced_ratio_reviewed: boolean; reduced_ratio_basis: string; notes: string;
  polygon: Coordinate[]; existing: Vent[]; proposed: Vent[];
}
export const VENT_TYPES: { value: VentKind; label: string }[] = [
  { value: 'soffit', label: 'Soffit intake' }, { value: 'edge', label: 'Roof / edge intake' },
  { value: 'ridge', label: 'Ridge vent' }, { value: 'box', label: 'Box / static roof vent' },
  { value: 'turbine', label: 'Turbine vent' }, { value: 'gable', label: 'Gable vent' },
  { value: 'powered', label: 'Powered exhaust fan' }, { value: 'other', label: 'Other' },
];
export const UNITS = { each: 'each', lf: 'linear ft', sqft: 'sq ft' };
export const newSpace = (name: string): AtticSpace => ({
  id: crypto.randomUUID(), name, area_sqft: '', ratio: 150, exhaust_percent: 50,
  reduced_ratio_reviewed: false, reduced_ratio_basis: '', notes: '', polygon: [], existing: [], proposed: [],
});
export const newVent = (): Vent => ({
  id: crypto.randomUUID(), name: 'Soffit intake', kind: 'soffit', role: 'intake', unit: 'each',
  quantity: '1', nfa_per_unit: null, cfm_per_unit: null, makeup_nfa_per_unit: null,
  available_quantity: null, location: '', rating_source: '', blocked: false, polygon: [], mapped_area_sqft: null,
});
const positive = (value: string | null) => value !== null && value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) > 0;
export const displayNumber = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Area ratios are NFVA only. CFM is never converted into passive exhaust area. */
export function calculateVentilation(space: AtticSpace, stage: Stage) {
  const area = positive(space.area_sqft) ? Number(space.area_sqft) : 0;
  const totalRequired = area * 144 / space.ratio;
  const exhaustTarget = totalRequired * space.exhaust_percent / 100;
  const intakeTarget = totalRequired - exhaustTarget;
  let intake = 0, exhaust = 0, cfm = 0, makeup = 0, powered = false;
  const warnings: string[] = [];
  if (!area) warnings.push('Enter the vented attic floor area.');
  if (space.ratio === 300 && (!space.reduced_ratio_reviewed || !space.reduced_ratio_basis.trim())) warnings.push('Record and review the local basis for using 1:300.');
  for (const vent of space[stage]) {
    const quantity = Number(vent.quantity);
    if (!vent.quantity.trim() || !Number.isFinite(quantity) || quantity < 0 || (vent.unit === 'each' && !Number.isInteger(quantity))) {
      warnings.push(`${vent.name}: enter a valid quantity.`); continue;
    }
    if (quantity === 0) continue;
    if (vent.blocked) { warnings.push(`${vent.name}: blocked or disconnected; excluded from capacity.`); continue; }
    if (vent.available_quantity !== null && quantity > Number(vent.available_quantity)) warnings.push(`${vent.name}: quantity exceeds the available opening / installation space.`);
    if (!vent.rating_source.trim()) warnings.push(`${vent.name}: record the manufacturer rating source.`);
    if (vent.kind === 'powered') {
      powered = true;
      if (positive(vent.cfm_per_unit)) cfm += quantity * Number(vent.cfm_per_unit);
      else warnings.push(`${vent.name}: enter the rated CFM per fan.`);
      if (positive(vent.makeup_nfa_per_unit)) makeup += quantity * Number(vent.makeup_nfa_per_unit);
      else warnings.push(`${vent.name}: enter the manufacturer's required intake NFVA per fan.`);
      continue;
    }
    if (!positive(vent.nfa_per_unit)) { warnings.push(`${vent.name}: NFVA rating is missing.`); continue; }
    const capacity = quantity * Number(vent.nfa_per_unit);
    if (vent.role === 'intake') intake += capacity; else exhaust += capacity;
  }
  const intakeDeficit = Math.max(0, intakeTarget - intake);
  const exhaustDeficit = Math.max(0, exhaustTarget - exhaust);
  const exhaustShare = intake + exhaust > 0 ? exhaust / (intake + exhaust) * 100 : 0;
  const balanced = intake + exhaust > 0 && exhaustShare >= 40 - 1e-7 && exhaustShare <= 50 + 1e-7;
  if (powered) {
    warnings.push('Powered system: verify fan sizing and makeup air with the manufacturer; CFM does not satisfy the passive area ratio.');
    if (exhaust > 0) warnings.push('Mixed powered and passive exhaust: review the airflow path with the manufacturer.');
    if (intake < makeup) warnings.push(`Powered intake shortfall: ${displayNumber(makeup - intake)} in² NFVA.`);
  }
  const meetsTargets = area > 0 && !powered && warnings.length === 0 && intakeDeficit < 1e-7 && exhaustDeficit < 1e-7 && balanced;
  return { totalRequired, intakeTarget, exhaustTarget, intake, exhaust, intakeDeficit, exhaustDeficit, exhaustShare, balanced, powered, cfm, makeup, warnings, meetsTargets };
}

export function additionalUnits(space: AtticSpace, vent: Vent): number | null {
  if (vent.kind === 'powered' || vent.blocked || !positive(vent.nfa_per_unit)) return null;
  const result = calculateVentilation(space, 'proposed');
  // Enough intake for both the selected target and the proposed exhaust.
  const deficit = vent.role === 'intake' ? Math.max(result.intakeDeficit, result.exhaust - result.intake) : result.exhaustDeficit;
  const factor = vent.unit === 'each' ? 1 : 100;
  return Math.max(0, Math.ceil((deficit / Number(vent.nfa_per_unit)) * factor - 1e-8) / factor);
}

export function validPlan(spaces: AtticSpace[]) {
  const number = (v: string | null, required = false, min = 0) => v === null ? !required : /^\d+(\.\d{1,3})?$/.test(v) && Number(v) >= min && Number(v) <= 999999999.999;
  return spaces.every(s => s.name.trim() && number(s.area_sqft, true, 0.001) && [...s.existing, ...s.proposed].every(v =>
    v.name.trim() && number(v.quantity, true) && (v.unit !== 'each' || Number.isInteger(Number(v.quantity))) &&
    number(v.nfa_per_unit, false, 0.001) && number(v.cfm_per_unit, false, 0.001) && number(v.makeup_nfa_per_unit, false, 0.001) && number(v.available_quantity)
  ));
}

export function ventilationSummary(spaces: AtticSpace[], address: string) {
  return [`VENTILATION PLAN — ${address}`, 'Planning targets only; verify site conditions, local requirements and product installation instructions.', ...spaces.flatMap(space => [
    `\n${space.name}: ${space.area_sqft} sq ft vented attic floor · 1:${space.ratio} · ${space.exhaust_percent}% exhaust target`,
    ...(space.ratio === 300 ? [`1:300 basis (${space.reduced_ratio_reviewed ? 'reviewed' : 'not reviewed'}): ${space.reduced_ratio_basis || 'Not recorded'}`] : []),
    ...(['existing', 'proposed'] as Stage[]).flatMap(stage => {
      const r = calculateVentilation(space, stage);
      return [stage.toUpperCase(), ...space[stage].map(v => `${v.name}: ${v.quantity} ${UNITS[v.unit]} · ${v.role} · ${v.kind === 'powered' ? `${v.cfm_per_unit ?? 'Unknown'} CFM/fan; ${v.makeup_nfa_per_unit ?? 'Unknown'} in² intake/fan` : `${v.nfa_per_unit ?? 'Unknown'} in² NFVA/${UNITS[v.unit]}`} · ${v.location} · ${v.rating_source}${v.blocked ? ' · BLOCKED' : ''}`),
        `Intake ${displayNumber(r.intake)} / ${displayNumber(r.intakeTarget)} in² target; passive exhaust ${displayNumber(r.exhaust)} / ${displayNumber(r.exhaustTarget)} in² target.`,
        r.meetsTargets ? 'Meets selected planning targets.' : 'Needs review.', ...r.warnings];
    }), `Notes: ${space.notes}`,
  ])].join('\n');
}
